import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
  UserProfile, 
  AuthContextType, 
  UserRole, 
  ROLE_PERMISSIONS,
  PERMISSIONS 
} from '../models/authTypes';
import { auth, db } from '../firebaseConfig';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User as FirebaseUser,
  GoogleAuthProvider,
  signInWithPopup,
  UserCredential
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp, getDocs, query, collection, where, deleteDoc } from 'firebase/firestore';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [currentUser, setCurrentUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  // Escuchar cambios en el estado de autenticación
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user: FirebaseUser | null) => {
      if (user) {
        // Usuario autenticado, obtener su perfil
        const userProfile = await getUserProfile(user.uid);
        setCurrentUser(userProfile);
      } else {
        setCurrentUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Obtener los permisos por defecto para un rol desde Firestore
  const getDefaultPermissionsForRole = async (role: UserRole): Promise<string[]> => {
    try {
      const rolesDoc = await getDoc(doc(db, 'system', 'roles'));
      if (rolesDoc.exists()) {
        const rolesData = rolesDoc.data();
        // Si hay permisos definidos para el rol en Firestore, los devolvemos
        if (rolesData && rolesData[role]) {
          return rolesData[role];
        }
      }
      // Si no hay configuración en Firestore, usamos los permisos por defecto
      return ROLE_PERMISSIONS[role] || [];
    } catch (error) {
      console.error('Error al obtener permisos por defecto:', error);
      return ROLE_PERMISSIONS[role] || [];
    }
  };

  // Obtener el perfil del usuario desde Firestore
  const getUserProfile = async (uid: string): Promise<UserProfile> => {
    const userDoc = await getDoc(doc(db, 'users', uid));
    const currentEmail = auth.currentUser?.email || '';
    
    // Verificar si el usuario es nuevo o está volviendo después de ser eliminado
    const isNewUser = !userDoc.exists();
    
    // Si el usuario no existe en Firestore, verificamos si hay algún documento antiguo con el mismo email
    // Esto puede ocurrir si el usuario fue eliminado en Authentication pero no en Firestore
    if (isNewUser && currentEmail) {
      try {
        // Buscar documentos con el mismo email en la colección users
        // Nota: Esto requiere un índice en Firestore para el campo 'email'
        // Si no tienes este índice, Firebase te dará un enlace para crearlo cuando ejecutes esta consulta
        const querySnapshot = await getDocs(
          query(collection(db, 'users'), where('email', '==', currentEmail))
        );
        
        // Si encontramos documentos antiguos, los eliminamos
        if (!querySnapshot.empty) {
          console.log(`Se encontraron ${querySnapshot.size} documentos antiguos para el email ${currentEmail}`);
          
          // Eliminar todos los documentos antiguos con el mismo email
          const deletePromises = querySnapshot.docs.map(doc => {
            console.log(`Eliminando documento antiguo con ID: ${doc.id}`);
            return deleteDoc(doc.ref);
          });
          
          await Promise.all(deletePromises);
          console.log('Documentos antiguos eliminados correctamente');
        }
      } catch (error) {
        console.error('Error al buscar o eliminar documentos antiguos:', error);
        // Continuamos con la creación del nuevo perfil aunque haya un error
      }
    }
    
    if (userDoc.exists()) {
      // Si el documento existe, actualizamos el último inicio de sesión
      const userData = userDoc.data() as UserProfile;
      await updateDoc(doc(db, 'users', uid), {
        lastLogin: new Date().toISOString(),
      });
      return userData;
    } else {
      // Si no existe el perfil, creamos uno por defecto con los permisos actuales
      const defaultPermissions = await getDefaultPermissionsForRole('usuario');
      
      const defaultUser: UserProfile = {
        uid,
        email: currentEmail,
        displayName: auth.currentUser?.displayName || '',
        photoURL: auth.currentUser?.photoURL || '',
        role: 'usuario',
        permissions: defaultPermissions,
        createdAt: serverTimestamp() as unknown as string,
        lastLogin: new Date().toISOString(),
      };
      
      // Guardar el perfil por defecto
      await setDoc(doc(db, 'users', uid), defaultUser);
      console.log(`Nuevo perfil creado para el usuario ${currentEmail} con permisos predeterminados`);
      return defaultUser;
    }
  };

  // Iniciar sesión con email y contraseña
  const signIn = async (email: string, password: string): Promise<void> => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const userProfile = await handleUserSignIn(userCredential);
      setCurrentUser(userProfile);
    } catch (error) {
      console.error('Error al iniciar sesión:', error);
      throw error;
    }
  };

  // Iniciar sesión con Google
  const signInWithGoogle = async (): Promise<void> => {
    try {
      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth, provider);
      const userProfile = await handleUserSignIn(userCredential);
      setCurrentUser(userProfile);
    } catch (error) {
      console.error('Error al iniciar sesión con Google:', error);
      throw error;
    }
  };

  // Manejar el inicio de sesión (común para ambos métodos)
  const handleUserSignIn = async (userCredential: UserCredential): Promise<UserProfile> => {
    const userId = userCredential.user.uid;
    let userProfile = await getUserProfile(userId);
    
    // Actualizar último inicio de sesión
    await updateDoc(doc(db, 'users', userId), {
      lastLogin: new Date().toISOString(),
      ...(userCredential.user.displayName && { displayName: userCredential.user.displayName }),
      ...(userCredential.user.photoURL && { photoURL: userCredential.user.photoURL }),
      ...(userCredential.user.email && { email: userCredential.user.email })
    });
    
    // Si es el correo del superadmin, asegurarse de que tenga el rol correcto
    if (userCredential.user.email === 'elgatodeescayola@gmail.com') {
      if (userProfile.role !== 'superadmin') {
        const superadminProfile = {
          ...userProfile,
          role: 'superadmin' as UserRole,
          permissions: ROLE_PERMISSIONS.superadmin
        };
        
        await updateDoc(doc(db, 'users', userId), {
          role: 'superadmin',
          permissions: ROLE_PERMISSIONS.superadmin
        });
        
        return superadminProfile;
      }
    }
    
    return userProfile;
  };

  // Cerrar sesión
  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      setCurrentUser(null);
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
      throw error;
    }
  };

  // Verificar si el usuario tiene un permiso específico
  const hasPermission = (permission: string): boolean => {
    if (!currentUser) return false;
    
    // El superadmin tiene todos los permisos
    if (currentUser.role === 'superadmin') return true;
    
    // Verificar si el permiso está en la lista de permisos del usuario
    return currentUser.permissions.includes(permission);
  };

  // Verificar si el usuario tiene al menos uno de los roles especificados
  const hasAnyRole = (roles: UserRole[]): boolean => {
    if (!currentUser) return false;
    return roles.includes(currentUser.role);
  };

  const value = {
    currentUser,
    loading,
    signIn,
    signInWithGoogle,
    signOut,
    hasPermission,
    hasAnyRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe usarse dentro de un AuthProvider');
  }
  return context;
};

// Componente de orden superior para proteger rutas
export const withAuth = <P extends object>(
  Component: React.ComponentType<P>,
  requiredPermission?: string,
  requiredRole?: UserRole[]
) => {
  const AuthenticatedComponent: React.FC<P> = (props) => {
    const { currentUser, hasPermission, hasAnyRole, loading } = useAuth();

    if (loading) {
      return <div>Cargando...</div>;
    }

    if (!currentUser) {
      // Redirigir a la página de inicio de sesión
      window.location.href = '/login';
      return null;
    }

    if (requiredPermission && !hasPermission(requiredPermission)) {
      return <div>No tienes permiso para acceder a esta página.</div>;
    }

    if (requiredRole && !hasAnyRole(requiredRole)) {
      return <div>No tienes el rol necesario para acceder a esta página.</div>;
    }

    return <Component {...props as P} />;
  };

  return AuthenticatedComponent;
};
